/**
 * Area element helpers (AST-87)
 *
 * Pure functions behind the "Map KMZ to Areas" screen. They turn the tree from
 * kmzParser.parseKmzFile into the elements saved to
 * PUT /projects/{id}/area-elements, and back.
 *
 * An element is one placemark: { id, section, name, lat, lng, geometryType,
 * kmzPath }. A folder selection expands to every placemark beneath it, each
 * tagged with the folder's path so the UI can group them again.
 */

export const SECTIONS = [
  { key: 'structures', label: 'Structures' },
  { key: 'yards', label: 'Project/Laydown Yards' },
  { key: 'other', label: 'Other' },
];

/**
 * Representative point for a GeoJSON geometry: the point itself, or the
 * centre of the bounding box for lines and polygons. Returns null when there
 * are no usable coordinates.
 */
export function representativePoint(geometry) {
  if (!geometry) return null;
  const pts = [];
  const walk = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      pts.push(c);
      return;
    }
    c.forEach(walk);
  };
  if (geometry.type === 'GeometryCollection') {
    (geometry.geometries || []).forEach((g) => walk(g?.coordinates));
  } else {
    walk(geometry.coordinates);
  }
  const valid = pts.filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat)
    && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180);
  if (!valid.length) return null;
  if (geometry.type === 'Point') return { lat: valid[0][1], lng: valid[0][0] };
  let minLat = Infinity; let maxLat = -Infinity; let minLng = Infinity; let maxLng = -Infinity;
  for (const [lng, lat] of valid) {
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
  }
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

/**
 * The shape to store for an element (AST-96): GeoJSON with altitude dropped
 * and coordinates rounded to 6 dp (~0.1 m), matching the backend.
 * GeometryCollections (KML MultiGeometry, in some exporters) are flattened to
 * the matching Multi* type when all parts are the same kind; a mixed
 * collection keeps its largest kind (polygons > lines > points). Returns null
 * when there's nothing drawable.
 */
export function storableGeometry(geometry) {
  if (!geometry) return null;
  const r = (v) => Math.round(v * 1e6) / 1e6;
  const pos = (c) => [r(c[0]), r(c[1])];
  const map = (c, d) => (d === 0 ? pos(c) : c.map((x) => map(x, d - 1)));
  const depth = { Point: 0, MultiPoint: 1, LineString: 1, MultiLineString: 2, Polygon: 2, MultiPolygon: 3 };

  if (geometry.type === 'GeometryCollection') {
    const parts = (geometry.geometries || []).map(storableGeometry).filter(Boolean);
    if (!parts.length) return null;
    const kind = (t) => (t.includes('Polygon') ? 'Polygon' : t.includes('LineString') ? 'LineString' : 'Point');
    const best = ['Polygon', 'LineString', 'Point'].find((k) => parts.some((g) => kind(g.type) === k));
    const same = parts.filter((g) => kind(g.type) === best);
    const flat = same.flatMap((g) => (g.type.startsWith('Multi') ? g.coordinates : [g.coordinates]));
    return flat.length === 1 ? { type: best, coordinates: flat[0] } : { type: `Multi${best}`, coordinates: flat };
  }
  if (!(geometry.type in depth) || !Array.isArray(geometry.coordinates)) return null;
  try {
    return { type: geometry.type, coordinates: map(geometry.coordinates, depth[geometry.type]) };
  } catch {
    return null;
  }
}

/**
 * Stable element id from the placemark's folder path, name and its ordinal
 * among same-named siblings, so re-uploading the same KMZ maps to the same ids
 * (the backend sees "unchanged", not "retire + create"). The ordinal matters:
 * real files repeat names within a folder (186 "Existing"/"Proposed" access
 * roads in one folder), and without it they'd collapse into one element.
 * Ids never contain '#'.
 */
export function elementIdFor(path, name, ordinal = 0) {
  const s = `${path}\u0000${name}\u0000${ordinal}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `pm-${h.toString(36)}-${s.length.toString(36)}`;
}

/**
 * Build lookups over a parsed KMZ tree:
 *   placemarks: Map(nodeId -> element-without-section)  (only those with coords)
 *   folders:    Map(nodeId -> { id, name, path, placemarkIds: [nodeId] })
 *   skipped:    number of placemarks with no usable coordinates
 */
export function indexTree(tree, features = []) {
  // kmzParser gives tree nodes and features unrelated ids (pm-<n>-<path> vs
  // feature-<i>). Both come from the same KML in document order -- togeojson
  // emits one feature per Placemark, and the tree walk visits Placemarks in
  // that same order -- so the n-th placemark node is feature n. Name is
  // checked as a guard; on a mismatch we fall back to the first unused
  // feature with the same name.
  let nth = 0;
  const unusedByName = new Map();
  features.forEach((f, i) => {
    const k = String(f.name);
    if (!unusedByName.has(k)) unusedByName.set(k, []);
    unusedByName.get(k).push(i);
  });
  const used = new Set();
  const featureFor = (node) => {
    const i = nth++;
    const f = features[i];
    if (f && !used.has(i) && (String(f.name) === node.name || /^Unnamed \d+$/.test(String(f.name)))) {
      used.add(i);
      return f;
    }
    const j = (unusedByName.get(node.name) || []).find((k) => !used.has(k));
    if (j === undefined) return null;
    used.add(j);
    return features[j];
  };

  const placemarks = new Map();
  const folders = new Map();
  let skipped = 0;

  const seenName = new Map(); // `${parentPath}\0${name}` -> count so far
  const visit = (node, parentPath) => {
    const path = parentPath ? `${parentPath} / ${node.name}` : node.name;
    if (node.type === 'placemark') {
      const geometry = featureFor(node)?.geometry;
      const nameKey = `${parentPath}\u0000${node.name}`;
      const ordinal = seenName.get(nameKey) || 0;
      seenName.set(nameKey, ordinal + 1);
      const pt = representativePoint(geometry);
      if (!pt) { skipped++; return []; }
      placemarks.set(node.id, {
        id: elementIdFor(path, node.name, ordinal),
        name: node.name,
        lat: pt.lat,
        lng: pt.lng,
        geometryType: node.geometryType || geometry?.type,
        kmzPath: parentPath || '',
        geometry: storableGeometry(geometry) || undefined,
        nodeId: node.id,
      });
      return [node.id];
    }
    const ids = (node.children || []).flatMap((c) => visit(c, path));
    folders.set(node.id, { id: node.id, name: node.name, path, placemarkIds: ids });
    return ids;
  };
  (tree || []).forEach((n) => visit(n, ''));
  return { placemarks, folders, skipped };
}

/**
 * Selections are what the user picked, per section:
 *   { structures: [{ kind: 'folder'|'placemark', nodeId }], yards: [...], other: [...] }
 * A node can be in only one section. Returns the elements to save, de-duplicated
 * (a placemark picked both directly and via its folder counts once, in the
 * section it was first assigned to).
 */
export function selectionsToElements(selections, index) {
  const out = new Map();
  for (const { key } of SECTIONS) {
    for (const sel of selections[key] || []) {
      const nodeIds = sel.kind === 'folder'
        ? (index.folders.get(sel.nodeId)?.placemarkIds || [])
        : [sel.nodeId];
      for (const nodeId of nodeIds) {
        const pm = index.placemarks.get(nodeId);
        if (!pm || out.has(pm.id)) continue;
        // eslint-disable-next-line no-unused-vars
        const { nodeId: _n, ...el } = pm;
        out.set(pm.id, { ...el, section: key });
      }
    }
  }
  return [...out.values()];
}

/** Every node id already used by a selection in any section (folders and their placemarks). */
export function usedNodeIds(selections, index) {
  const used = new Map(); // nodeId -> section key
  for (const { key } of SECTIONS) {
    for (const sel of selections[key] || []) {
      used.set(sel.nodeId, key);
      if (sel.kind === 'folder') {
        for (const id of index.folders.get(sel.nodeId)?.placemarkIds || []) used.set(id, key);
      }
    }
  }
  return used;
}

/**
 * Nodes still pickable in a section: not used by another section, and not a
 * folder that contains anything used elsewhere (so a folder can't silently
 * steal a placemark already assigned to another section).
 */
export function availableNodes(sectionKey, selections, index) {
  const used = usedNodeIds(selections, index);
  const takenElsewhere = (id) => used.has(id) && used.get(id) !== sectionKey;
  const alreadyHere = new Set((selections[sectionKey] || []).map((s) => s.nodeId));

  const folders = [...index.folders.values()]
    .filter((f) => f.placemarkIds.length > 0)
    .filter((f) => !alreadyHere.has(f.id) && !takenElsewhere(f.id))
    .filter((f) => !f.placemarkIds.some(takenElsewhere))
    .map((f) => ({ kind: 'folder', nodeId: f.id, label: f.path, count: f.placemarkIds.length }));

  const placemarks = [...index.placemarks.entries()]
    .filter(([id]) => !alreadyHere.has(id) && !used.has(id))
    .map(([id, pm]) => ({ kind: 'placemark', nodeId: id, label: pm.kmzPath ? `${pm.kmzPath} / ${pm.name}` : pm.name }));

  return [...folders, ...placemarks];
}

/**
 * Rebuild selections from saved elements (reopening the screen). Without the
 * original file we can't know which folder was picked, so saved elements are
 * grouped by their kmzPath into read-only "saved group" entries. If a parsed
 * tree is available, saved elements are matched to its placemarks by id.
 */
export function elementsToSelections(elements) {
  const selections = { structures: [], yards: [], other: [] };
  const groups = new Map();
  for (const e of elements || []) {
    if (!selections[e.section]) continue;
    const key = `${e.section}\u0000${e.kmzPath || ''}`;
    if (!groups.has(key)) {
      const g = { kind: 'saved', nodeId: `saved:${key}`, section: e.section, label: e.kmzPath || '(top level)', elements: [] };
      groups.set(key, g);
      selections[e.section].push(g);
    }
    groups.get(key).elements.push(e);
  }
  return selections;
}

/** Elements to save when selections mix saved groups with newly picked nodes. */
export function buildSavePayload(selections, index) {
  const fresh = index ? selectionsToElements(selections, index) : [];
  const ids = new Set(fresh.map((e) => e.id));
  const kept = [];
  for (const { key } of SECTIONS) {
    for (const sel of selections[key] || []) {
      if (sel.kind !== 'saved') continue;
      for (const e of sel.elements) {
        if (ids.has(e.id)) continue;
        ids.add(e.id);
        // Saved shapes go back unchanged (or are omitted, which the backend
        // treats as "keep the stored shape").
        // AST-98: and keep their own source file -- an element mapped from an
        // earlier upload still came from that upload.
        kept.push({
          id: e.id, section: key, name: e.name, lat: e.lat, lng: e.lng, geometryType: e.geometryType, kmzPath: e.kmzPath,
          ...(e.geometry ? { geometry: e.geometry } : {}),
          ...(e.sourceFileKey ? { sourceFileKey: e.sourceFileKey } : {}),
        });
      }
    }
  }
  return [...kept, ...fresh];
}

/** Display count for a selection entry. */
export function selectionCount(sel, index) {
  if (sel.kind === 'saved') return sel.elements.length;
  if (sel.kind === 'folder') return index?.folders.get(sel.nodeId)?.placemarkIds.length || 0;
  return 1;
}
